import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

type LogLevel = 'debug' | 'error' | 'info' | 'warning';

type MonitoringContext = {
  extra?: Record<string, unknown>;
  fingerprint?: string[];
  level?: LogLevel;
  tags?: Record<string, string | number | boolean | undefined>;
};

const MAX_CONTEXT_DEPTH = 4;
const MAX_STRING_LENGTH = 1000;
const SENSITIVE_KEY_PATTERN = /authorization|cookie|password|secret/iu;
const SENSITIVE_QUERY_PATTERN =
  /([?&#][^=&#\s]*(?:code|email|jwt|token)[^=&#\s]*=)[^&#\s]*/giu;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu;
const JWT_PATTERN = /\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\b/gu;

const contextFrom = (contextOrLevel?: MonitoringContext | LogLevel) =>
  typeof contextOrLevel === 'string'
    ? { level: contextOrLevel }
    : (contextOrLevel ?? {});

const toDatadogStatus = (level: LogLevel = 'error') =>
  level === 'warning' ? 'warn' : level;

const getDatadogFingerprint = (fingerprint: string[] | undefined) =>
  fingerprint?.filter(Boolean).join('|');

const isSensitiveKey = (key: string) => {
  const normalizedKey = key.toLowerCase();

  return (
    SENSITIVE_KEY_PATTERN.test(normalizedKey) ||
    normalizedKey === 'email' ||
    normalizedKey.endsWith('email') ||
    normalizedKey.endsWith('email_address') ||
    normalizedKey.endsWith('jwt') ||
    normalizedKey.endsWith('token')
  );
};

const sanitizeString = (value: string, maxLength = MAX_STRING_LENGTH) =>
  value
    .replace(SENSITIVE_QUERY_PATTERN, '$1[redacted]')
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(JWT_PATTERN, '[redacted-jwt]')
    .slice(0, maxLength);

const sanitizeMonitoringValue = (value: unknown, depth = 0): unknown => {
  if (value instanceof Error) {
    return {
      message: sanitizeString(value.message),
      name: value.name,
    };
  }

  if (typeof value === 'string') return sanitizeString(value);

  if (typeof value !== 'object' || value === null) return value;

  if (depth >= MAX_CONTEXT_DEPTH) return '[truncated]';

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMonitoringValue(item, depth + 1));
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      isSensitiveKey(key)
        ? '[redacted]'
        : sanitizeMonitoringValue(item, depth + 1),
    ]),
  );
};

const sanitizeMonitoringRecord = (record: Record<string, unknown> = {}) =>
  sanitizeMonitoringValue(record) as Record<string, unknown>;

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    const sanitizedError = new Error(sanitizeString(error.message));
    sanitizedError.name = error.name;
    sanitizedError.stack = error.stack
      ? sanitizeString(error.stack, Number.POSITIVE_INFINITY)
      : undefined;
    return sanitizedError;
  }

  try {
    return new Error(JSON.stringify(sanitizeMonitoringValue(error)));
  } catch {
    return new Error(sanitizeString(String(error)));
  }
};

const getErrorMetadata = (error: unknown): Record<string, unknown> => {
  if (typeof error !== 'object' || error === null) return {};

  const errorRecord = error as {
    code?: unknown;
    config?: { method?: unknown; url?: unknown };
    data?: { error?: unknown; reason_code?: unknown; reasonCode?: unknown };
    response?: {
      data?: { error?: unknown; reason_code?: unknown; reasonCode?: unknown };
      status?: unknown;
    };
    status?: unknown;
  };
  const data = errorRecord.response?.data ?? errorRecord.data;

  return sanitizeMonitoringRecord({
    api_error_code: data?.error,
    api_error_reason_code: data?.reason_code ?? data?.reasonCode,
    error_code: errorRecord.code,
    http_status: errorRecord.status ?? errorRecord.response?.status,
    request_method: errorRecord.config?.method,
    request_url: errorRecord.config?.url,
  });
};

const buildDatadogContext = (context: MonitoringContext) => {
  const fingerprint = getDatadogFingerprint(context.fingerprint);
  const tags = sanitizeMonitoringRecord(context.tags);

  return {
    ...sanitizeMonitoringRecord(context.extra),
    ...tags,
    'error.fingerprint': fingerprint,
    error_fingerprint: fingerprint,
    tags,
  };
};

export const captureException = (
  error: unknown,
  contextOrLevel?: MonitoringContext | LogLevel,
) => {
  const context = contextFrom(contextOrLevel);
  const normalizedError = normalizeError(error);
  const datadogContext = {
    ...buildDatadogContext(context),
    ...getErrorMetadata(error),
  };
  const fingerprint = getDatadogFingerprint(context.fingerprint);

  if (fingerprint) {
    (normalizedError as Error & { dd_fingerprint?: string }).dd_fingerprint =
      fingerprint;
  }

  datadogRum.addError(normalizedError, datadogContext);
  datadogLogs.logger.error(
    normalizedError.message,
    datadogContext,
    normalizedError,
  );
};

export const captureMessage = (
  message: string,
  contextOrLevel?: MonitoringContext | LogLevel,
) => {
  const context = contextFrom(contextOrLevel);
  const datadogContext = buildDatadogContext(context);
  const status = toDatadogStatus(context.level);

  datadogLogs.logger[status](
    sanitizeString(message, Number.POSITIVE_INFINITY),
    datadogContext,
  );
};

export const setErrorMonitoringUser = (user: {
  email?: string;
  id?: string | number;
}) => {
  const id = user.id ? String(user.id) : undefined;

  if (!id) {
    datadogRum.clearUser();
    datadogLogs.clearUser();
    return;
  }

  const datadogUser = { id };

  datadogRum.setUser(datadogUser);
  datadogLogs.setUser(datadogUser);
};
